import styled from "styled-components";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "../main/component/Card";
import { findTest, getRelatedTests } from "../../data/data";
import { useEffect, useMemo, useRef, useState } from "react";
import KakaoImg from "../../assets/image/kakaotalk_shareing.png";
import DisplayAds from "../../components/DisplayAdsFlex";
import { ResultDocument } from "../../components/result-document/ResultDocument";
import { Recommendations } from "../../components/Recommendations";
import { TraitChart } from "../../story/TraitChart";
import { decodeScores, decodeTops, parseScript, topTrait } from "../../story/parseScript";
import { InsightCard } from "../../story/InsightCard";
import { RadarMap, groupPct } from "../../story/RadarMap";
import { tx } from "../../story/texts";
import { ReadingMap, ReadingSave } from "../../story/ReadingMap";

const SITE_URL = "https://mango.moistmarketer.com";
const TOAST_MS = 1500;

export const Result = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, count } = useParams();
  const data = findTest(id);
  // 문답형은 숫자 count, 스토리형은 문자 key 로 결과를 찾음
  const result = data?.result.find((item) => `${item.key ?? item.count}` === `${count}`);
  const [searchParams] = useSearchParams();
  const story = useMemo(
    () => (data?.kind === "story" ? parseScript(data.script) : null),
    [data]
  );
  const storyScores = story ? decodeScores(searchParams.get("s"), story.traits) : null;
  // 묶음이 있는 스토리: 묶음마다 그래프, 두 번째 묶음부터는 가장 높은 습관 설명 카드
  const groupTitle = (name) => data?.groupTitles?.[name] || name;
  // 묶음별 1위: 플레이어가 정한 값(t)을 우선, 없으면 점수로 계산
  const groupTops = story
    ? decodeTops(searchParams.get("t"), story.groups) ||
      story.groups.map((g, i) =>
        i === 0 ? result?.trait : topTrait(storyScores || {}, g.traits)
      )
    : [];
  const extraInsights =
    story && storyScores
      ? story.groups.slice(1).map((g, i) => ({
          label: groupTitle(g.name),
          insight: data.insights?.[groupTops[i + 1]],
        }))
      : [];

  // 긴 결과글 없이 분포 지도만 보여주는 스토리 (resultMode: "chart")
  const chartMode = !!story && data?.resultMode === "chart";
  // 검사 대신 '읽기 지도': 이야기 조각별 몰입도 → 블로그 글 추천 (resultMode: "reading")
  const readingMode = !!story && data?.resultMode === "reading";

  const [toastMsg, setToastMsg] = useState("");
  const toastTimer = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id, count]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // 없는 테스트는 메인으로, 없는 결과 번호는 해당 테스트 소개로
  if (!data) return <Navigate to="/" replace />;
  if (!result) return <Navigate to={`/test-main/${data.id}`} replace />;

  const testUrl = `${SITE_URL}/test-main/${data.id}`;
  const resultUrl = `${SITE_URL}${location.pathname}${location.search}`;

  const showToast = (message) => {
    setToastMsg(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), TOAST_MS);
  };

  const shareKakao = () => {
    if (!window.Kakao?.Share) {
      showToast("카카오톡 공유를 사용할 수 없습니다.");
      return;
    }
    window.Kakao.Share.sendDefault({
      objectType: "feed",
      content: {
        title: data.title,
        description: data.description,
        imageUrl: data.shareImage,
        link: { mobileWebUrl: testUrl, webUrl: testUrl },
      },
      buttons: [
        {
          title: "유형검사 시작",
          link: { mobileWebUrl: testUrl, webUrl: testUrl },
        },
        {
          title: "공유된 결과보기",
          link: { mobileWebUrl: resultUrl, webUrl: resultUrl },
        },
      ],
    });
  };

  return (
    <TestContainer>
      {readingMode && <ReadingMap test={data} recommend={result.recommend} />}
      {chartMode && (
        <RadarMap
          title={data.title}
          groups={story.groups.map((g) => ({ ...g, title: groupTitle(g.name) }))}
          scores={storyScores}
          tops={groupTops}
          summaries={story.groups.map((g, i) => {
            const top = groupTops[i];
            const info = i === 0 ? result : data.insights?.[top];
            if (!top || !info) return null;
            return {
              label: groupTitle(g.name),
              type: info.type,
              subtitle: info.subtitle,
              pct: groupPct(storyScores, g.traits)[top],
            };
          })}
        />
      )}
      {!chartMode && !readingMode && story && story.groups.length === 0 && (
        <TraitChart scores={storyScores} traits={story.traits} highlight={result.trait} />
      )}
      {!chartMode && story &&
        story.groups.map((g, i) => (
          <TraitChart
            key={g.name}
            title={groupTitle(g.name)}
            scores={storyScores}
            traits={g.traits}
            highlight={i === 0 ? result.trait : groupTops[i]}
          />
        ))}
      {chartMode || readingMode ? null : result.body ? (
        // 글로 작성한 결과지 (새 형식)
        <ResultDocument test={data} result={result} />
      ) : (
        // 통이미지 결과지 (기존 형식)
        <>
          <ResultImg src={result.image} alt={result.type || data.title} />
          <ResultType>{result.type}</ResultType>
          <Description>{result.description}</Description>
        </>
      )}

      {!chartMode && extraInsights.map((x) => (
        <InsightCard key={x.label} label={x.label} insight={x.insight} />
      ))}

      {/* 읽기 지도는 책·상품을 지도 안에서 따로 보여줌 */}
      {!readingMode && <Recommendations recommend={result.recommend} />}

      <div
        style={{
          marginTop: 36,
          fontSize: 19,
          fontWeight: 400,
        }}
      >
        {tx(data, "shareTitle")}
      </div>
      <ShareContainer>
        {data.kakaoShareId && (
          <ShareItem onClick={shareKakao}>
            <img className="share__icon" src={KakaoImg} alt="카카오톡" />
            <div className="share__title">카카오톡</div>
          </ShareItem>
        )}
        <CopyToClipboard
          text={testUrl}
          onCopy={() => showToast(tx(data, "toastShare"))}
        >
          <ShareItem>
            <div
              className="share__icon"
              style={{
                border: "1px solid gray",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              URL
            </div>
            <div className="share__title">{tx(data, "shareCopy")}</div>
          </ShareItem>
        </CopyToClipboard>

        <ShareItem
          onClick={() => {
            window.location.href =
              "https://blog.naver.com/moistmarketer/223225286890";
          }}
        >
          <div
            className="share__icon"
            style={{
              border: "1px solid gray",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            ETC
          </div>
          <div className="share__title">{tx(data, "shareEtc")}</div>
        </ShareItem>
      </ShareContainer>

      <StartBtn
        type="button"
        style={{ marginTop: 24 }}
        onClick={() => {
          navigate(`/test-main/${data.id}`);
        }}
      >
        {tx(data, "retry")}
      </StartBtn>

      <TestList>
        {getRelatedTests(data).map((item, index) => (
          <FlexColumn key={item.id}>
            {index % 3 === 0 && <DisplayAds />}
            <Card cardItem={item} />
          </FlexColumn>
        ))}
      </TestList>
      {/* 읽기 지도: 페이지 맨 아래에 '한 번에 저장하기' */}
      {readingMode && searchParams.get("r") && <ReadingSave test={data} rValue={searchParams.get("r")} />}
      <ToastMsg className={toastMsg ? "toast__show" : ""}>
        {toastMsg || "\u00a0"}
      </ToastMsg>
    </TestContainer>
  );
};

const TestContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ResultImg = styled.img`
  width: 100%;
  height: auto;
  margin: 24px auto;
  max-width: 600px;
  display: block;
  border-radius: 15px;
`;

const ResultType = styled.div`
  width: 100%;
  text-align: center;

  color: var(--light-text01, #1f2022);

  /* heading/18-sb */
  font-family: 'GyeonggiBatang';

  font-size: 24px;
  font-style: normal;
  font-weight: 400;
`;
const Description = styled.pre`
  width: 100%;
  padding: 0 24px;
  margin-top: 10px;
  color: var(--light-text01, #1f2022);

  word-break: break-all;
  white-space: break-spaces;

  /* heading/18-sb */
  font-family: 'GyeonggiBatang';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  
`;

const StartBtn = styled.button`
  width: 222px;
  height: 50px;
  min-height: 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  border-radius: 30px;
  border: 0;

  background-color: #133337;

  margin: 0 auto;

  font-family: 'GyeonggiBatang';
  font-size: 15px;
  font-weight: 400;
  color: white;

  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000), 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  transition: all 0.2s ease;

  &:hover {
    background-color: #130a3e;
  }
`;

const TestList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  margin-top: 48px;
  padding-bottom: 24px;
`;

const ShareContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 10px;
`;

const ShareItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;

  cursor: pointer;

  .share__icon {
    width: 48px;
    min-width: 48px;
    max-width: 48px;
    height: 48px;
    min-height: 48px;
    max-height: 48px;
    border-radius: 50%;
  }
  .share__title {
    font-size: 14px;
  }
`;

const ToastMsg = styled.div`
  position: fixed;

  width: max-content;
  height: 40px;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);

  border-radius: 20px;
  padding: 12px 24px;

  line-height: 1;
  font-size: 15px;
  font-weight: 400;

  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  transition: all 0.2s ease-in;

  &.toast__show {
    bottom: 24px;
  }
`;

const FlexColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;